import { QueryKeys } from '../../../queryDefaults/QueryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppMedError } from '../../../error/AppMedError.ts';
import { useIdFromRoute } from '../../../hooks/useIdFromRoute.tsx';
import {
    HealthcareProfessional,
    IHealthcareProfessional,
    IHealthcareProfessionalPayload,
} from '../HealthcareProfessional.ts';
import { HealthcareProfessionalsApi } from '../../../apis/HealthcareProfessionalsApi/HealthcareProfessionalsApi.ts';
import { useSessionProvider } from '../../../provider/sessionContext.tsx';
import { healthcareProfessionalNewQuery } from '../queries/useGetHealthcareProfessionalNew.tsx';

export function usePutHealthcareProfessional() {
    const queryClient = useQueryClient();
    const id = useIdFromRoute();
    const { currentUser, setCurrentUser } = useSessionProvider();

    return useMutation<IHealthcareProfessional, AppMedError, IHealthcareProfessionalPayload>({
        mutationKey: [QueryKeys.healthcareProfessional, id],
        mutationFn: async (variables: IHealthcareProfessionalPayload) => {
            await HealthcareProfessionalsApi.putHealthcareProfessionalById(variables, id!);
            const roleCatalogue = await queryClient.ensureQueryData(healthcareProfessionalNewQuery);
            const professional = new HealthcareProfessional(variables as unknown as HealthcareProfessional);
            return {
                ...professional,
                id: Number(id),
                name: `${professional.firstName} ${professional.lastName}`,
                healthcareProfessionalType:
                    roleCatalogue?.healthcareProfessionalTypeLabel(professional.healthcareProfessionalTypeID) ?? '',
            } as unknown as IHealthcareProfessional;
        },
        onSuccess: (healthcareProfessional: IHealthcareProfessional) => {
            queryClient.setQueryData<IHealthcareProfessional>(
                [QueryKeys.healthcareProfessional, id],
                healthcareProfessional,
            );

            queryClient.setQueryData<IHealthcareProfessional[]>([QueryKeys.healthcareProfessionals], (prev) => {
                return prev
                    ? prev.map((hp) => (hp.id == healthcareProfessional.id ? healthcareProfessional : hp))
                    : [healthcareProfessional];
            });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.healthcareProfessionals] });
            setCurrentUser({
                ...currentUser,
                displayName: `${healthcareProfessional.firstName} ${healthcareProfessional.lastName}`,
            });
            localStorage.setItem(
                'currentUser',
                JSON.stringify({
                    ...currentUser,
                    displayName: `${healthcareProfessional.firstName} ${healthcareProfessional.lastName}`,
                }),
            );
        },
    });
}
